import type { MixpanelEventMap } from './types'
import {
  getAugmentedProperties,
  shouldSendEvent,
  isNtpNewsEventContentType,
} from './helper'
import { sendEvent } from './sendEvent'

async function getEventData(
  eventName: string,
  data: Record<string, unknown> = {}
) {
  const siteSettingsStore = useSiteSettingsStore()
  const { data: siteSettings } = storeToRefs(siteSettingsStore)
  const augmentedProperties = await getAugmentedProperties(
    siteSettings,
    eventName
  )

  return { ...augmentedProperties, ...data }
}

function debounceRenderedTilesEvent(delay = 300) {
  const newsTileRenderedCount = ref({ article: 0, video: 0, game: 0 })
  let timeout: NodeJS.Timeout | null = null

  function debounce(contentType: 'article' | 'video' | 'game') {
    if (timeout) clearTimeout(timeout)

    newsTileRenderedCount.value[contentType] += 1

    timeout = setTimeout(async () => {
      const eventData = await getEventData('NtpNewsTile_Rendered', {
        article_tiles_rendered_count: newsTileRenderedCount.value.article,
        video_tiles_rendered_count: newsTileRenderedCount.value.video,
        game_tiles_rendered_count: newsTileRenderedCount.value.game,
      })

      sendEvent('NtpNewsTile_Rendered', eventData)

      newsTileRenderedCount.value = { article: 0, video: 0, game: 0 }
      timeout = null
    }, delay)
  }

  return debounce
}

const sendRenderedNewsTileEvent = debounceRenderedTilesEvent()

export const useAnalytics = () => {
  async function track<K extends keyof MixpanelEventMap>(
    name: string,
    data?: MixpanelEventMap[K]
  ): Promise<void> {
    if (!shouldSendEvent(name, data)) return
    const { client } = await useOpenFeature()
    client.track(name, data)
    if (
      name === 'NtpNewsTile_Rendered' &&
      data &&
      isNtpNewsEventContentType(data)
    ) {
      sendRenderedNewsTileEvent(data.ntp_content_type)
    } else {
      const eventData = await getEventData(name, data)
      try {
        await sendEvent(name, eventData)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        /* empty */
      }
    }
  }

  return { track, sendMixpanelEvent: track }
}
